/**
 * Interactive Site Map Drawer Plugin (mobile only)
 *
 * Mounts `[data-panel]` into the drawer starting in a collapsed (72 px peek) state.
 * Tap expands to full open; swipe-down snaps back to collapsed — both behaviours are
 * owned by `drawer.js` via the `peek` option passed to `mountElement`.
 *
 * `restorePanel` is registered so the panel node is moved back to its original parent
 * before `drawerContainer.innerHTML` is cleared, preventing the node from being destroyed.
 *
 * When to touch this file:
 *  - Wiring the partial/full Redux subscription (`selectDrawerMode` → `expandToFullSize` /
 *    `collapseToPartial`) when the full-size drawer state is introduced.
 *  - Any new drawer lifecycle hooks (chip changes, view transitions, etc.).
 *  - If `drawerService` API changes (mountElement, onHtmlRemoved, clearHtml).
 *
 * Mobile-only is enforced by gating on `context.state.isMobile` 
 */
export const DRAWER_ID = 'native-interactive-site-map-drawer';
const PEEK_HEIGHT = '54px';

export default function createInteractiveSiteMapDrawerPlugin() {
  const state = {
    mounted: false,
  };

  return {
    name: DRAWER_ID,

    async start(context) {
      if (!context.state.isMobile) {
        return;
      }

      const { drawerService } = context.services;
      if (!drawerService) {
        return;
      }

      const panelElement = context.root.querySelector('[data-panel]');
      if (!panelElement) {
        return;
      }

      const originalParent = panelElement.parentElement;

      function restorePanel() {
        if (panelElement.parentElement !== originalParent) {
          originalParent.appendChild(panelElement);
        }
      }

      // Register restorePanel BEFORE mountElement to guarantee it is in place
      // before the drawer can be closed. Race: clearHtml could be dispatched
      // between mountElement and onHtmlRemoved registration if reversed.
      drawerService.onHtmlRemoved({ name: DRAWER_ID, callback: restorePanel });
      drawerService.mountElement(panelElement, { peek: PEEK_HEIGHT });
      state.mounted = true;
    },

    async stop(context) {
      if (!state.mounted) {
        return;
      }
      context.services.drawerService?.clearHtml?.();
      state.mounted = false;
    },
  };
}
